/**
 * BN-010 — Saneado y medición del cuerpo rich text de la landing del banner.
 *
 * El editor es `contentEditable`, así que el HTML que sale de él depende del navegador: Chrome
 * arma `<div>` por línea, Safari mete `<span style>`, y un pegado trae lo que haya en el
 * portapapeles. Lo que se guarda tiene que ser un subconjunto chico y fijo, porque lo renderiza
 * la app mobile: `<p>`, `<br>`, `<b>`, `<i>` y `<a href>`. Todo lo demás se desarma conservando
 * el texto.
 *
 * Además es la única defensa real del `dangerouslySetInnerHTML` de la vista previa: el saneado
 * se aplica al escribir Y al renderizar, para que un valor que llegó de la API sin pasar por el
 * editor tampoco pueda inyectar nada.
 */

const ALLOWED_TAGS = new Set(["P", "BR", "B", "I", "A"]);

/**
 * Tags que se tiran CON su contenido, en vez de desarmarse.
 *
 * El resto se desarma conservando el texto, porque ahí el texto es lo que el operador escribió.
 * Acá no: el contenido de un `<script>` o un `<style>` no es texto visible en ningún lado, y
 * desarmarlo volcaba el código como párrafo del cuerpo al pegar desde una página cualquiera.
 */
const DROPPED_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "NOSCRIPT",
  "TEMPLATE",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "HEAD",
  "TITLE",
]);

/** Normaliza a un único tag por intención: el mobile no tiene que entender dos formas de negrita. */
const TAG_ALIASES: Record<string, string> = { DIV: "P", STRONG: "B", EM: "I" };

/** Mismo criterio que `ctaValue` en el resto del formulario de banners. */
const ALLOWED_LINK_PROTOCOLS = ["http:", "https:"];

export const ALLOWED_LINK_PROTOCOLS_MESSAGE =
  "Tiene que ser una URL que empiece con http o https";

/**
 * Solo URLs absolutas con protocolo permitido. `new URL` sin base tira en las relativas y
 * normaliza los trucos de escape (`java\tscript:`), así que `javascript:` cae acá.
 */
export const isAllowedRichTextHref = (href: string): boolean => {
  try {
    return ALLOWED_LINK_PROTOCOLS.includes(new URL(href).protocol);
  } catch {
    return false;
  }
};

const copyAllowedChildren = (source: Node, doc: Document, target: Node): void => {
  source.childNodes.forEach((child) => {
    if (child.nodeType === 3) {
      target.appendChild(doc.createTextNode(child.nodeValue ?? ""));
      return;
    }
    if (child.nodeType !== 1) return;

    const element = child as Element;
    /**
     * `tagName` viene en MAYUSCULAS solo en HTML: dentro de `<svg>` o `<math>` el parser conserva
     * la caja original, asi que `<svg><script>` llegaba como "script" y se colaba por la rama de
     * "desarmar conservando el texto" — volcando el codigo del script como texto del cuerpo.
     */
    const tagName = element.tagName.toUpperCase();
    if (DROPPED_TAGS.has(tagName)) return;

    const tag = TAG_ALIASES[tagName] ?? tagName;

    // Tag no permitido: se desarma y se queda el texto, en vez de perder lo que escribieron.
    if (!ALLOWED_TAGS.has(tag)) {
      copyAllowedChildren(element, doc, target);
      return;
    }

    if (tag === "BR") {
      target.appendChild(doc.createElement("br"));
      return;
    }

    if (tag === "A") {
      const href = element.getAttribute("href") ?? "";
      if (!isAllowedRichTextHref(href)) {
        copyAllowedChildren(element, doc, target);
        return;
      }
      // Solo `href`: cómo se abre el link lo decide quien renderiza (la app mobile), no el
      // contenido que carga Operaciones.
      const anchor = doc.createElement("a");
      anchor.setAttribute("href", href);
      copyAllowedChildren(element, doc, anchor);
      target.appendChild(anchor);
      return;
    }

    const copy = doc.createElement(tag.toLowerCase());
    copyAllowedChildren(element, doc, copy);
    target.appendChild(copy);
  });
};

export const sanitizeBannerRichText = (html: string): string => {
  if (!html) return "";
  // `parseFromString` arma un documento inerte: no corre scripts ni pide recursos.
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const container = parsed.createElement("div");
  copyAllowedChildren(parsed.body, parsed, container);
  container.normalize();
  return container.innerHTML;
};

/**
 * Largo del cuerpo en TEXTO VISIBLE, que es lo que cuenta el límite duro de 1000.
 *
 * Contar el HTML almacenado haría que poner una negrita descuente 7 caracteres del presupuesto
 * del operador, que no es lo que significa "1000 caracteres" en el ticket. El editor mide con
 * `textContent` sobre el DOM vivo y esto mide `textContent` sobre el HTML guardado: son el mismo
 * número, así que el contador en pantalla y el error de validación nunca se contradicen.
 *
 * La API tiene que aplicar el MISMO criterio (BN-020): contar texto plano, no el string HTML.
 */
export const bannerLandingBodyLength = (html: string): number => {
  if (!html) return 0;
  // Se mide sobre el HTML SANEADO, que es lo que se guarda: si no, un pegado con markup de mas
  // podria contar caracteres que despues no quedan en ningun lado.
  const parsed = new DOMParser().parseFromString(sanitizeBannerRichText(html), "text/html");
  return (parsed.body.textContent ?? "").length;
};

/** Un cuerpo que solo tiene markup vacío (`<p><br></p>`) está vacío para el usuario. */
export const isBannerRichTextEmpty = (html: string): boolean => {
  if (!html) return true;
  const parsed = new DOMParser().parseFromString(sanitizeBannerRichText(html), "text/html");
  return (parsed.body.textContent ?? "").trim() === "";
};

/** Recorta texto plano al presupuesto que queda. Lo usa el pegado del editor. */
export const clampToRemaining = (text: string, remaining: number): string =>
  text.slice(0, Math.max(0, remaining));
